import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles, MessageCircle, CalendarCheck } from 'lucide-react';
import { HERO_ACTIVITY_CHIPS, MADE_ON_VENDALIST } from './homeDummyContent';

const STEPS = [
  {
    icon: Sparkles,
    title: 'Browse the look',
    text: 'Photo-first listings with clear packages.',
  },
  {
    icon: MessageCircle,
    title: 'Talk details',
    text: 'Message vendors about date, vibe, and setup.',
  },
  {
    icon: CalendarCheck,
    title: 'Book with clarity',
    text: 'Confirm pricing before you commit.',
  },
];

/**
 * Full-height discovery panel — PeerSpace map slot, Vendalist lookbook instead.
 */
const MarketingBrowseAside = ({
  locationLabel = null,
  countLabel = null,
  className = '',
}) => {
  const mosaic = useMemo(() => MADE_ON_VENDALIST.slice(0, 4), []);
  const [active, setActive] = useState(0);
  const hero = mosaic[active] || mosaic[0];

  return (
    <aside
      className={`marketing-browse-aside ${className}`.trim()}
      aria-label="Marketplace lookbook"
    >
      <div className="marketing-browse-aside-hero">
        {hero?.image ? (
          <img
            key={hero.id}
            src={hero.image}
            alt={hero.title}
            className="marketing-browse-aside-hero-img"
          />
        ) : null}
        <div className="marketing-browse-aside-hero-shade" />

        <div className="marketing-browse-aside-hero-top">
          <div>
            <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-white/70">
              Lookbook
            </p>
            <p className="mt-1 text-sm font-medium text-white">
              {locationLabel ? `Near ${locationLabel}` : 'Made on Vendalist'}
            </p>
          </div>
          {countLabel ? (
            <span className="rounded-full bg-white/15 px-2.5 py-1 text-[11px] font-medium text-white">
              {countLabel}
            </span>
          ) : null}
        </div>

        <div className="marketing-browse-aside-hero-bottom">
          <p className="line-clamp-2 text-lg font-medium tracking-tight text-white sm:text-xl">
            {hero?.title}
          </p>
          <p className="mt-1 text-sm text-white/75">{hero?.meta}</p>
          <div className="mt-4 flex gap-1.5">
            {mosaic.map((item, index) => (
              <button
                key={item.id}
                type="button"
                aria-label={`Show ${item.title}`}
                onClick={() => setActive(index)}
                className={`h-1 flex-1 transition-colors ${
                  index === active ? 'bg-white' : 'bg-white/35 hover:bg-white/55'
                }`}
              />
            ))}
          </div>
        </div>
      </div>

      <div className="marketing-browse-aside-body">
        <div className="grid grid-cols-4 gap-1">
          {mosaic.map((item, index) => (
            <button
              key={`thumb-${item.id}`}
              type="button"
              onClick={() => setActive(index)}
              className={`relative aspect-square overflow-hidden ${
                index === active ? 'ring-2 ring-inset ring-m-ink' : 'opacity-75 hover:opacity-100'
              }`}
            >
              <img
                src={item.image}
                alt=""
                className="h-full w-full object-cover"
                loading="lazy"
              />
            </button>
          ))}
        </div>

        <div className="mt-5">
          <p className="mb-3 text-sm font-medium text-m-ink">Explore by vibe</p>
          <div className="flex flex-wrap gap-2">
            {HERO_ACTIVITY_CHIPS.slice(0, 6).map((chip) => (
              <Link
                key={chip.q}
                to={`/search?q=${encodeURIComponent(chip.q)}`}
                className="rounded-full border border-m-border-light bg-white px-3.5 py-2 text-[13px] font-medium text-m-ink transition-colors hover:border-m-ink"
              >
                {chip.label}
              </Link>
            ))}
          </div>
        </div>

        <div className="mt-6 space-y-4 border-t border-m-border-light pt-5">
          {STEPS.map(({ icon: Icon, title, text }) => (
            <div key={title} className="flex gap-3">
              <span className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-m-canvas-light text-m-ink">
                <Icon className="h-4 w-4" aria-hidden />
              </span>
              <div className="min-w-0">
                <p className="text-sm font-medium text-m-ink">{title}</p>
                <p className="mt-0.5 text-[13px] leading-snug text-m-ink-muted">
                  {text}
                </p>
              </div>
            </div>
          ))}
        </div>

        <Link
          to="/vendor-signup"
          className="mt-6 flex h-12 items-center justify-between rounded-full bg-m-ink px-5 text-sm font-medium text-white transition-colors hover:bg-[#1a1a1a]"
        >
          List your services
          <ArrowRight className="h-4 w-4" aria-hidden />
        </Link>
      </div>
    </aside>
  );
};

export default MarketingBrowseAside;
